import React from 'react';
import { Search, X, Filter } from 'lucide-react';
import { PipelineStatus } from '../../types/pipeline';

interface PipelineFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: PipelineStatus | 'ALL';
  onStatusFilterChange: (status: PipelineStatus | 'ALL') => void;
  showDisabled: boolean;
  onShowDisabledChange: (show: boolean) => void;
  ownerFilter: 'SELF' | 'ALL';
  onOwnerFilterChange: (filter: 'SELF' | 'ALL') => void;
  onClearFilters: () => void;
}

export const PipelineFilters: React.FC<PipelineFiltersProps> = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  showDisabled,
  onShowDisabledChange,
  ownerFilter,
  onOwnerFilterChange,
  onClearFilters,
}) => {
  const hasActiveFilters = searchQuery || statusFilter !== 'ALL' || showDisabled || ownerFilter === 'ALL';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search Input */}
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search pipelines..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Status Filter Dropdown */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as PipelineStatus | 'ALL')}
            className="block pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="status-filter"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="ERROR">Error</option>
            <option value="DISABLED">Disabled</option>
          </select>
        </div>

        {/* Owner Filter Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Show:</span>
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => onOwnerFilterChange('SELF')}
              className={`px-4 py-2 text-sm font-medium border rounded-l-md transition-colors ${
                ownerFilter === 'SELF'
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              data-testid="owner-filter-self"
            >
              My Pipelines
            </button>
            <button
              type="button"
              onClick={() => onOwnerFilterChange('ALL')}
              className={`px-4 py-2 text-sm font-medium border rounded-r-md transition-colors ${
                ownerFilter === 'ALL'
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              data-testid="owner-filter-all"
            >
              All Pipelines
            </button>
          </div>
        </div>

        {/* Show Disabled Checkbox */}
        <div className="flex items-center">
          <input
            id="show-disabled"
            type="checkbox"
            checked={showDisabled}
            onChange={(e) => onShowDisabledChange(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            data-testid="show-disabled-checkbox"
          />
          <label
            htmlFor="show-disabled"
            className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
          >
            Show disabled
          </label>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            data-testid="clear-filters-button"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
};

